<template>
    <transition name="slide-down">
        <div
            v-if="banner"
            class="NotificationBanner"
            :class="['is-' + banner.type]"
            role="alert"
            aria-live="polite"
        >
            <div class="NotificationBanner-content">
                <i :class="iconClass"></i>
                <div class="NotificationBanner-text">
                    <h4 v-if="banner.title" class="NotificationBanner-title">
                        {{ banner.title }}
                    </h4>
                    <p class="NotificationBanner-message">{{ banner.message }}</p>
                </div>
                <div class="NotificationBanner-actions">
                    <a
                        v-if="banner.actionUrl && banner.actionLabel"
                        :href="banner.actionUrl"
                        class="NotificationBanner-action"
                        @click="handleActionClick"
                    >
                        {{ banner.actionLabel }}
                    </a>
                    <button
                        class="NotificationBanner-close"
                        @click="handleDismiss"
                        aria-label="Banner schließen"
                    >
                        <i class="ri-close-line"></i>
                    </button>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
export default {
    name: 'NotificationBanner',
    props: {
        banner: {
            type: Object,
            default: null,
        },
    },
    computed: {
        iconClass() {
            if (!this.banner) return '';
            const iconMap = {
                info: 'ri-information-line',
                warning: 'ri-error-warning-line',
                success: 'ri-check-line',
                error: 'ri-close-circle-line',
            };
            return iconMap[this.banner.type] || iconMap.info;
        },
    },
    methods: {
        handleDismiss() {
            if (this.banner && this.banner.id) {
                this.$store.dispatch('notificationBanners/dismissBanner', this.banner.id);
            }
        },
        handleActionClick(event) {
            // Allow default link behavior, but also track if needed
            this.$emit('action-clicked', {
                banner: this.banner,
                url: this.banner.actionUrl,
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.NotificationBanner {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 800;
    width: 100%;
    padding: 12px 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    animation: slideDown 0.3s ease-out;

    @media (max-width: $breakpoint-tablet-portrait) {
        padding: 10px 12px;
    }

    &-content {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        max-width: 1200px;
        margin: 0 auto;
        width: 100%;

        @media (max-width: $breakpoint-tablet-portrait) {
            gap: 8px;
        }
    }

    i {
        font-size: 1.25rem;
        flex-shrink: 0;
        margin-top: 2px;

        @media (max-width: $breakpoint-tablet-portrait) {
            font-size: 1.125rem;
        }
    }

    &-text {
        flex: 1;
        min-width: 0;
    }

    &-title {
        margin: 0 0 4px 0;
        font-size: 0.9375rem;
        font-weight: 600;
        line-height: 1.4;

        @media (max-width: $breakpoint-tablet-portrait) {
            font-size: 0.875rem;
        }
    }

    &-message {
        margin: 0;
        font-size: 0.875rem;
        line-height: 1.5;
        color: var(--color-text-black);

        @media (max-width: $breakpoint-tablet-portrait) {
            font-size: 0.8125rem;
        }
    }

    &-actions {
        display: flex;
        align-items: center;
        gap: 12px;
        flex-shrink: 0;

        @media (max-width: $breakpoint-tablet-portrait) {
            gap: 8px;
        }
    }

    &-action {
        padding: 6px 12px;
        border-radius: 4px;
        font-size: 0.875rem;
        font-weight: 500;
        text-decoration: none;
        transition: background-color 0.2s ease, opacity 0.2s ease;
        white-space: nowrap;
        color: var(--color-text-white);

        @media (max-width: $breakpoint-tablet-portrait) {
            padding: 5px 10px;
            font-size: 0.8125rem;
        }

        &:hover {
            opacity: 0.9;
        }
    }

    &-close {
        background: transparent;
        border: none;
        padding: 4px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        transition: background-color 0.2s ease, opacity 0.2s ease;
        opacity: 0.8;

        i {
            font-size: 1.125rem;
            margin: 0;
        }

        &:hover {
            opacity: 1;
            background-color: rgba(0, 0, 0, 0.1);
        }

        &:focus {
            outline: 2px solid currentColor;
            outline-offset: 2px;
        }
    }

    &.is-info {
        background-color: rgba(var(--color-blue-rgb), 0.1);
        border-bottom: 0.5px solid var(--color-blue-light);
        color: var(--color-blue-dark);

        i {
            color: var(--color-blue);
        }

        .NotificationBanner-title {
            color: var(--color-blue-dark);
        }

        .NotificationBanner-action {
            background-color: var(--color-blue);
            color: var(--color-text-white);

            &:hover {
                background-color: var(--color-blue-dark);
            }
        }

        .NotificationBanner-close {
            color: var(--color-blue);

            &:hover {
                background-color: rgba(var(--color-blue-rgb), 0.1);
            }
        }
    }

    &.is-warning {
        background-color: var(--color-yellow-light);
        border-bottom: 0.5px solid var(--color-warning);
        color: var(--color-orange-dark);

        i {
            color: var(--color-warning);
        }

        .NotificationBanner-title {
            color: var(--color-orange-dark);
        }

        .NotificationBanner-action {
            background-color: var(--color-warning);
            color: var(--color-text-black);

            &:hover {
                background-color: var(--color-orange);
            }
        }

        .NotificationBanner-close {
            color: var(--color-warning);

            &:hover {
                background-color: rgba(255, 181, 63, 0.1);
            }
        }
    }

    &.is-error {
        background-color: var(--color-red);
        border-bottom: 0.5px solid var(--color-red);
        color: var(--color-text-white);

        i {
            color: var(--color-text-white);
        }

        .NotificationBanner-title {
            color: var(--color-text-white);
        }

        .NotificationBanner-message {
            color: var(--color-text-white);
        }

        .NotificationBanner-action {
            background-color: rgba(255, 255, 255, 0.2);
            color: var(--color-text-white);

            &:hover {
                background-color: rgba(255, 255, 255, 0.3);
            }
        }

        .NotificationBanner-close {
            color: var(--color-text-white);

            &:hover {
                background-color: rgba(255, 255, 255, 0.2);
            }
        }
    }

    &.is-success {
        background-color: rgba(var(--color-success-rgb), 0.1);
        border-bottom: 0.5px solid var(--color-green);
        color: var(--color-green-dark);

        i {
            color: var(--color-success);
        }

        .NotificationBanner-title {
            color: var(--color-green-dark);
        }

        .NotificationBanner-action {
            background-color: var(--color-success);
            color: var(--color-text-white);

            &:hover {
                background-color: var(--color-green-dark);
            }
        }

        .NotificationBanner-close {
            color: var(--color-success);

            &:hover {
                background-color: rgba(var(--color-success-rgb), 0.1);
            }
        }
    }
}

@keyframes slideDown {
    from {
        transform: translateY(-100%);
        opacity: 0;
    }
    to {
        transform: translateY(0);
        opacity: 1;
    }
}

.slide-down-enter-active {
    transition: all 0.3s ease-out;
}

.slide-down-leave-active {
    transition: all 0.3s ease-in;
}

.slide-down-enter,
.slide-down-leave-to {
    transform: translateY(-100%);
    opacity: 0;
}
</style>
